import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BudgetSummary, Client, Project, ProjectBudget } from '@bpd/shared';
import { formatCurrencyRounded } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';

type LoadState = 'loading' | 'ready' | 'error' | 'not-configured';

const ZERO_BUDGET: Omit<ProjectBudget, 'projectId' | 'clientId' | 'gates'> = {
  estimatedRemaining: 0,
  contractorEstimatesShare: 0,
  selfSourcedMaterialsShare: 0,
  invoicedTotal: 0,
  scheduledTotal: 0,
  paidTotal: 0,
  totalProjectCost: 0,
  remainingBalanceDue: 0,
};

/** Same six figures the rest of the app already computes via computeBottomLine (see
 * ProjectDetailPage's "See breakdown" and invoiceMath.ts's doc comment) — this page just lays
 * them out as one dense, statement-style table across every client/project instead of a per-page
 * ledger. Column order mirrors a real income statement: the three components that sum to Cost,
 * then Paid subtracted down to Balance. "Contractor estimates" and "Self-sourced materials" are
 * the same terms already used on the project header's breakdown (not "accepted estimate" /
 * "product" verbatim) so this page never disagrees with the per-project one. Each client row IS
 * its totals row (collapsed by default) — the caret only reveals/hides its per-project breakdown
 * underneath; clicking anywhere else on a client or project row navigates there. */
function sumBudgets(
  rows: Omit<ProjectBudget, 'projectId' | 'clientId' | 'gates'>[],
): Omit<ProjectBudget, 'projectId' | 'clientId' | 'gates'> {
  return rows.reduce(
    (acc, r) => ({
      estimatedRemaining: acc.estimatedRemaining + r.estimatedRemaining,
      contractorEstimatesShare: acc.contractorEstimatesShare + r.contractorEstimatesShare,
      selfSourcedMaterialsShare: acc.selfSourcedMaterialsShare + r.selfSourcedMaterialsShare,
      invoicedTotal: acc.invoicedTotal + r.invoicedTotal,
      scheduledTotal: acc.scheduledTotal + r.scheduledTotal,
      paidTotal: acc.paidTotal + r.paidTotal,
      totalProjectCost: acc.totalProjectCost + r.totalProjectCost,
      remainingBalanceDue: acc.remainingBalanceDue + r.remainingBalanceDue,
    }),
    { ...ZERO_BUDGET },
  );
}

function MoneyCells({ b }: { b: Omit<ProjectBudget, 'projectId' | 'clientId' | 'gates'> }) {
  return (
    <>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {formatCurrencyRounded(b.contractorEstimatesShare)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {formatCurrencyRounded(b.selfSourcedMaterialsShare)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {formatCurrencyRounded(b.invoicedTotal)}
      </td>
      <td className="px-3 py-1.5 text-right font-bold tabular-nums text-phoenix-blue-900">
        {formatCurrencyRounded(b.totalProjectCost)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-blue-700">
        {formatCurrencyRounded(b.scheduledTotal)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrencyRounded(b.paidTotal)}</td>
      <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-orange-700">
        {formatCurrencyRounded(b.remainingBalanceDue)}
      </td>
    </>
  );
}

export function FinancialOverviewPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>('loading');
  const [clients, setClients] = useState<Client[]>([]);
  const [projectsByClient, setProjectsByClient] = useState<Record<string, Project[]>>({});
  const [budget, setBudget] = useState<BudgetSummary | null>(null);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());

  function toggleExpand(clientId: string) {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }

  useEffect(() => {
    if (!projectsApi.isConfigured()) {
      setState('not-configured');
      return;
    }
    setState('loading');
    projectsApi
      .listClients()
      .then(async (clientList) => {
        const [projectLists, budgetSummary] = await Promise.all([
          Promise.all(clientList.map((c) => projectsApi.listProjects(c.id))),
          projectsApi.getBudget(),
        ]);
        const byClient: Record<string, Project[]> = {};
        clientList.forEach((c, i) => {
          byClient[c.id] = projectLists[i] ?? [];
        });
        setClients(clientList);
        setProjectsByClient(byClient);
        setBudget(budgetSummary);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  const grandTotal = budget ? sumBudgets(budget.clientTotals) : ZERO_BUDGET;

  return (
    <main className="mx-auto max-w-6xl px-6 py-16">
      <h1 className="font-serif text-3xl font-bold text-phoenix-blue-900">Financial Overview</h1>
      <p className="mt-2 text-sm text-gray-500">
        Every client and project, rolled up the same way as each project&apos;s own budget
        breakdown.
      </p>

      {state === 'not-configured' && (
        <p className="mt-8 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          The project management backend isn&apos;t connected yet in this environment.
        </p>
      )}
      {state === 'error' && (
        <p className="mt-8 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn&apos;t load the financial overview. Try again in a moment.
        </p>
      )}
      {state === 'loading' && <p className="mt-8 text-sm text-gray-500">Loading…</p>}

      {state === 'ready' && budget && (
        <>
          {/* The one number that matters most on this page, called out on its own before the dense
              table below — everything in the table is supporting detail for this figure. */}
          <div className="mt-6 rounded-lg border border-phoenix-blue-200 bg-phoenix-blue-50 px-6 py-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-phoenix-blue-500">
              Total cost — every client, every project
            </p>
            <p className="mt-1 font-serif text-4xl font-bold tabular-nums text-phoenix-blue-900">
              {formatCurrencyRounded(grandTotal.totalProjectCost)}
            </p>
          </div>
          <div className="mt-6 overflow-x-auto rounded-md border border-gray-200 bg-white">
            <table className="w-full min-w-[58rem] border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-gray-300 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 text-left">Client / Project</th>
                  <th className="px-3 py-2 text-right">Contractor Estimates</th>
                  <th className="px-3 py-2 text-right">Self-Sourced Materials</th>
                  <th className="px-3 py-2 text-right">Invoiced</th>
                  <th className="px-3 py-2 text-right text-sm font-extrabold text-phoenix-blue-700">
                    Cost
                  </th>
                  <th className="px-3 py-2 text-right">Scheduled</th>
                  <th className="px-3 py-2 text-right">Paid</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-sm text-gray-400">
                      No clients yet.
                    </td>
                  </tr>
                )}
                {clients.map((client) => {
                  const projects = projectsByClient[client.id] ?? [];
                  const clientTotal =
                    budget.clientTotals.find((ct) => ct.clientId === client.id) ?? ZERO_BUDGET;
                  const isExpanded = expandedClients.has(client.id);
                  return (
                    <Fragment key={client.id}>
                      <tr
                        onClick={() =>
                          navigate(`/admin/projects/clients/${client.id}`, {
                            state: { from: 'financials' },
                          })
                        }
                        className="cursor-pointer border-t-2 border-gray-300 bg-gray-50 font-semibold hover:bg-gray-100"
                      >
                        <td className="px-3 py-1.5">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(client.id);
                            }}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            className="mr-2 text-xs text-phoenix-blue-500"
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                          <span className="font-serif text-phoenix-blue-900">{client.label}</span>
                        </td>
                        <MoneyCells b={clientTotal} />
                      </tr>
                      {isExpanded && projects.length === 0 && (
                        <tr>
                          <td
                            colSpan={8}
                            className="px-3 py-1.5 pl-10 text-sm italic text-gray-400"
                          >
                            No projects yet
                          </td>
                        </tr>
                      )}
                      {isExpanded &&
                        projects.map((project) => {
                          const pb =
                            budget.projects.find((b) => b.projectId === project.id) ?? ZERO_BUDGET;
                          return (
                            <tr
                              key={project.id}
                              onClick={() =>
                                navigate(`/admin/projects/clients/${client.id}/${project.id}`, {
                                  state: { from: 'financials' },
                                })
                              }
                              className="cursor-pointer border-t border-gray-100 hover:bg-gray-50"
                            >
                              <td className="px-3 py-1.5 pl-10 text-gray-700 hover:text-phoenix-blue-600">
                                {project.name}
                              </td>
                              <MoneyCells b={pb} />
                            </tr>
                          );
                        })}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-phoenix-blue-900 text-base">
                  <td className="px-3 py-2 font-serif font-bold text-phoenix-blue-900">Total</td>
                  <MoneyCells b={grandTotal} />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </main>
  );
}
